import React from 'react';
import { observer } from 'mobx-react-lite';
import { UploadImg } from 'src/components/UploadImg/UploadImg';
import { HomePageCarouselRedirectOptions } from 'src/main/cms/HomePageCarousel/HomePageCarouselForm/HomePageCarouselRedirectOptions';
import { HomePageCarouselFilter } from './HomePageCarouselFilter';
import { Popup } from 'src/domains/layouts/shared/popup/Popup';
import { SuccessPopup } from 'src/domains/layouts/shared/popup/popups/cms/SuccessPopup';
import { FormComponent, FormWrapper } from 'src/domains/layouts/shared/cms/form/Form.style';
import { HomePageCarouselState } from 'src/AppState/CmsState/HomePageCarouselState/HomePageCarouselState';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Select } from 'src/domains/layouts/shared/formComponents/Select/components/Select';
import { ParagraphDefault } from 'src/domains/layouts/shared/typography/Content.style';
import { PromoCarouselTranslationsForm } from './PromoCarouselTranslationsForm';
import { AddTranslationButton } from 'src/domains/layouts/shared/cms/translationsForm/CmsTranslationsForm.style';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { Dates } from 'src/main/cms/Dates/Dates';

interface HomePageCarouselBonusEnginePromoFormPropsType {
    carouselPromoState: HomePageCarouselState;
    dataTest: string;
}

export const HomePageCarouselBonusEnginePromoForm = observer(
    ({ carouselPromoState, dataTest }: HomePageCarouselBonusEnginePromoFormPropsType) => {
        const {
            title,
            subtitle,
            btnTitle,
            btnColor,
            bgColor,
            tcText,
            actionTemplate,
            redirectOptions,
            campaignIdState,
            campaignsListOptions,
            campaignsListLoading,
            onCampaignSelect,
            refreshCampaigns,
            selectedDevice,
            deviceOptions,
            uploadIcon,
            handleCreate,
            handleReset,
            handleEdit,
            isEditMode,
            isLoading,
            onChangeDevice,
            onChangeActionTemplate,
            handleHideCreatedPopup,
            showCreatedPopup,
            isDefaultPromo,
            languagesState,
            isCreateProcessing,
        } = carouselPromoState;

        const common = useCommon();
        const configState = ConfigState.get(common);

        return (
            <FormWrapper
                isEditMode={isEditMode}
                data-test={dataTest}
            >
                <HomePageCarouselFilter carouselPromoState={carouselPromoState} />
                {/* TODO: Iris to update the description */}
                <ParagraphDefault>
                    Bonus Engine Promos are cards which are linked to a bonus engine campaign. The card can contain an
                    opt-in button that requires the user to click before being eligible for the campaign.
                </ParagraphDefault>

                <FormComponent
                    onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                        e.preventDefault();
                    }}
                    data-test='form-component'
                >
                    <Loader isActive={isLoading} />

                    <Select
                        defaultText='Select campaign'
                        stateValue={campaignIdState}
                        onClick={refreshCampaigns}
                        selectOptions={campaignsListOptions}
                        loading={campaignsListLoading}
                        dataTest='select-campaign'
                        label='Campaign'
                        onChangeCB={onCampaignSelect}
                    />
                    <Textarea
                        formState={title}
                        isEmpty={title.value === ''}
                        label='Title'
                        dataTest='promo-title'
                    />
                    <Textarea
                        formState={subtitle}
                        isEmpty={subtitle.value === ''}
                        label='Subtitle'
                        dataTest='promo-subtitle'
                    />
                    <Input
                        formInputState={btnTitle}
                        isEmpty={btnTitle.value === ''}
                        label='Button call to action text'
                        dataTest='call-to-action-button-text'
                    />

                    <>
                        {carouselPromoState.formsIds.map((item) => {
                            const state = carouselPromoState.getTranslationFormStateById(item);
                            return (
                                <PromoCarouselTranslationsForm
                                    key={item}
                                    promoTranslationsState={state}
                                    promoCarouselState={carouselPromoState}
                                    languagesState={languagesState}
                                    isDefaultPromo={isDefaultPromo}
                                    formId={item}
                                />
                            );
                        })}
                        {carouselPromoState.formsIds.length === 0 &&
                        languagesState.getNonDefaultLanguagesList().length > 0 ? (
                            <AddTranslationButton
                                addAnotherForm={false}
                                version='secondary'
                                width='full'
                                onClick={carouselPromoState.addLanguageForm}
                            >
                                Add language
                            </AddTranslationButton>
                        ) : null}
                    </>

                    <Input
                        formInputState={btnColor}
                        isEmpty={btnColor.value === ''}
                        label='Button color (#hex value only)'
                        dataTest='button-colour'
                    />

                    <Select
                        stateValue={actionTemplate}
                        selectOptions={redirectOptions}
                        defaultText='Select redirection'
                        onChangeCB={onChangeActionTemplate}
                        dataTest='select-redirection-homepage'
                    />

                    <HomePageCarouselRedirectOptions carouselPromoState={carouselPromoState} />

                    <Input
                        formInputState={bgColor}
                        isEmpty={bgColor.value === ''}
                        label='Background color (#hex value only)'
                        dataTest='background-colour'
                    />

                    <UploadImg
                        title='Image'
                        uploadImageState={uploadIcon}
                        hideDeleteButton={configState.config.hideDeleteImageButtonOnHomePageCarousel}
                    />

                    <Textarea
                        formState={tcText}
                        isEmpty={tcText.value === ''}
                        label='Terms'
                        ckeditor
                    />

                    <Dates
                        carouselPromoState={carouselPromoState}
                        changeConfirmable
                    />

                    <Select
                        label='Select device'
                        stateValue={selectedDevice}
                        selectOptions={deviceOptions}
                        defaultText='Select device'
                        onChangeCB={onChangeDevice}
                    />

                    <FormButtons
                        onCreate={handleCreate}
                        isCreateDisabled={isCreateProcessing}
                        onUpdate={handleEdit}
                        onCancel={handleReset}
                        isEditMode={isEditMode}
                        dataTestCreate='create-button'
                    />

                    {showCreatedPopup === false ? null : (
                        <Popup
                            width={384}
                            handleHidePopup={handleHideCreatedPopup}
                        >
                            <SuccessPopup
                                handleHidePopup={handleHideCreatedPopup}
                                itemLabel='promo carousel'
                            />
                        </Popup>
                    )}
                </FormComponent>
            </FormWrapper>
        );
    }
);
